import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Class } from '@pirouette/shared';
import { Empty } from '@/components/ui/Empty';
import { supabase } from '@/lib/supabase';
import { formatTime12 } from '@/lib/time';

export const Route = createFileRoute('/faculty/')({
  component: FacultyToday,
});

function useFacultyTodayClasses() {
  return useQuery({
    queryKey: ['faculty-today-classes'],
    queryFn: async (): Promise<Class[]> => {
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;
      if (!userId) return [];

      const { data: instructorRow, error: instructorError } = await supabase
        .from('instructors')
        .select('id')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (instructorError) throw instructorError;
      if (!instructorRow) return [];

      const { data, error } = await supabase
        .from('class_instructors')
        .select('classes(*)')
        .eq('instructor_id', instructorRow.id);
      if (error) throw error;

      interface Row {
        classes: Class | null;
      }
      return ((data ?? []) as Row[])
        .map((r) => r.classes)
        .filter((c): c is Class => c !== null && c.deleted_at === null);
    },
  });
}

function FacultyToday() {
  const classes = useFacultyTodayClasses();

  return (
    <div className="space-y-8">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Today's roster
        </p>
        <h1 className="mt-2">Classes</h1>
      </header>

      {classes.isLoading && <p aria-live="polite">Loading…</p>}

      {classes.data?.length === 0 && (
        <Empty
          title="No classes assigned"
          description="Ask the studio admin to add you to a class."
        />
      )}

      {classes.data && classes.data.length > 0 && (
        <ul className="grid gap-3">
          {classes.data.map((c) => (
            <li key={c.id} className="surface-elevated p-4">
              <Link
                to="/faculty/classes/$id/attendance"
                params={{ id: c.id }}
                className="flex flex-wrap items-baseline justify-between gap-2"
              >
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-3 text-[var(--color-ink-muted)] text-[var(--text-sm)] capitalize">
                    {c.weekday} {formatTime12(c.start_time)}–{formatTime12(c.end_time)} · {c.room}
                  </span>
                </span>
                <span className="text-[var(--color-accent)] text-[var(--text-sm)]">
                  Mark attendance →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
